import React, { useState } from "react";
import { MdShoppingCart } from "react-icons/md";
import { AiOutlineUserAdd } from "react-icons/ai";
import { BsPeople } from "react-icons/bs";
import { MdCheckCircle } from "react-icons/md";
import EAModal from "../../../shared/Modal/EAModal";
import EAButton from "../../../shared/Buttons/EAButton";
import EASkeletonLoader from "../../../shared/EASkeletonLoader";
import useCourtReservationModal from "./useCourtReservationModal";
import { SaleStep } from "../../../../store/stepSaleConfig/stepSaleConfigReducer";
import "./style.scss";
import useAuth from "../../../Login/hooks/useAuth";
import { LOGIN_PAGE, LOGIN_PAGE_WITH_RETURN } from "../../../../routes";
import EALink from "../../../shared/Link/EALink";
import CourtTicketSelection from "./CourtTicketSelection/CourtTicketSelection";
import PassSelection from "./PassSelection/PassSelection";
import useCourtPassRegistration from "../../hooks/useCourtPassRegistration";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  exerciseId: number | null;
  saleStep: SaleStep;
  onTicketAdded?: (exerciseId: number) => void;
  onDataRefresh?: () => Promise<void>;
}

type ModalStep = "info" | "tickets" | "pass-selection" | "pass-confirmation";

const CourtReservationModal: React.FC<IProps> = ({
  isOpen,
  onClose,
  exerciseId,
  saleStep,
  onTicketAdded,
  onDataRefresh,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    tickets,
    exerciseDetails,
    loading,
    ticketAccess,
    ticketLimit,
    allowToSignByPass,
    getModalTitle,
    getCapacityText,
    getBuyButtonTitle,
    isSaleAvailable,
    getSaleUnavailableMessage,
  } = useCourtReservationModal({ isOpen, exerciseId, saleStep });
  const { isLoggedIn } = useAuth();
  const [currentStep, setCurrentStep] = useState<ModalStep>("info");
  const [lastPassAction, setLastPassAction] = useState<{ type: "register" | "unregister" | null }>({ type: null });
  const [loginRequiredModalOpen, setLoginRequiredModalOpen] = useState(false);

  // Hook do zapisywania karnetem
  const { loading: passLoading, alert: passAlert, joinToExerciseByPass, clearAlert } = useCourtPassRegistration();

  // Reset step when modal closes or exerciseId changes
  React.useEffect(() => {
    if (!isOpen) {
      setCurrentStep("info");
      setLastPassAction({ type: null });
      setLoginRequiredModalOpen(false);
    }
  }, [isOpen]);

  React.useEffect(() => {
    setCurrentStep("info");
  }, [exerciseId]);

  if (!exerciseId) return null;

  const handleBuyTicket = () => {
    if (saleStep?.loginRequired && !isLoggedIn) {
      setLoginRequiredModalOpen(true);
    } else {
      setCurrentStep("tickets");
    }
  };

  const handleSignWithPass = () => {
    clearAlert();
    setCurrentStep("pass-selection");
  };

  const handleBackToInfo = () => {
    setCurrentStep("info");
  };

  const handleTicketsAdded = () => {
    if (onTicketAdded && exerciseId) {
      onTicketAdded(exerciseId);
    }
    onClose();
  };

  const handlePassSelected = async (passId: string, assigned: boolean = false) => {
    if (!exerciseId) return;

    // Zapisz typ operacji dla późniejszego wyświetlenia potwierdzenia
    setLastPassAction({ type: assigned ? "unregister" : "register" });

    const success = await joinToExerciseByPass(exerciseId, passId, assigned, async () => {
      // Refresh capacity after successful registration/unregistration
      if (onTicketAdded && exerciseId) {
        onTicketAdded(exerciseId);
      }

      // Refresh court grid data to show updated reservations
      if (onDataRefresh) {
        await onDataRefresh();
      }
    });

    if (success) {
      setCurrentStep("pass-confirmation");
    }
  };

  const handleContinue = () => {
    onClose();
  };

  const renderInfoSection = () => (
    <div className="court-reservation-modal__info">
      {/* <h5 className="court-reservation-modal__title">{exerciseDetails?.exerciseGroup.displayName}</h5> */}
      {saleStep?.showOtherTicketsPool && (
        <div className="court-reservation-modal__chip">
          <BsPeople />
          <span>{getCapacityText()}</span>
        </div>
      )}
    </div>
  );

  const renderPassConfirmationStep = () => {
    const getConfirmationContent = () => {
      if (lastPassAction.type === "register") {
        return {
          title: "Zapisano karnetem",
          message: "Zostałeś pomyślnie zapisany na zajęcia karnetem.",
        };
      } else if (lastPassAction.type === "unregister") {
        return {
          title: "Wypisano z zajęć",
          message: "Zostałeś pomyślnie wypisany z zajęć.",
        };
      }
      return {
        title: "Potwierdzenie",
        message: "Operacja została wykonana pomyślnie.",
      };
    };

    const content = getConfirmationContent();

    return (
      <div className="court-reservation-modal__confirmation">
        <div className="court-reservation-modal__confirmation-icon">
          <MdCheckCircle />
        </div>
        <h4>{content.title}</h4>
        <p>{content.message}</p>
      </div>
    );
  };

  const getModalTitleByStep = () => {
    switch (currentStep) {
      case "tickets":
        return t("wybierz_bilety");
      case "pass-selection":
        return "Wybierz karnet, którym chcesz się zapisać";
      case "pass-confirmation":
        return t("potwierdzenie");
      default:
        return getModalTitle();
    }
  };

  const getModalProps = () => {
    if (currentStep === "pass-confirmation") {
      // Dla operacji karnetem (zapisywanie/wypisywanie) - tylko przycisk "OK"
      return {
        hideButtons: false,
        hideAcceptButton: true,
        cancelButtonLabel: "OK",
        onAccept: handleContinue,
      };
    }
    if (currentStep === "tickets") {
      return {
        onBack: handleBackToInfo,
        hideButtons: true,
      };
    }
    return {
      hideButtons: true,
    };
  };

  const handleModalClose = () => {
    if (currentStep === "pass-confirmation") {
      handleContinue();
    } else {
      onClose();
    }
  };

  return (
    <>
      <EAModal
        isOpen={isOpen}
        onClose={handleModalClose}
        title={getModalTitleByStep()}
        desktopWidth="660px"
        {...getModalProps()}
      >
        <div className="court-reservation-modal">
          {loading ? (
            <EASkeletonLoader height="200px" />
          ) : exerciseDetails ? (
            <div className="court-reservation-modal__content">
              {currentStep === "info" && (
                <>
                  {exerciseDetails.eventMessage && (
                    <img
                      src={exerciseDetails.eventMessage}
                      alt={exerciseDetails.name}
                      className="court-reservation-modal__image"
                    />
                  )}

                  {renderInfoSection()}

                  {exerciseDetails.description && (
                    <div
                      className="court-reservation-modal__description"
                      dangerouslySetInnerHTML={{ __html: exerciseDetails.description }}
                    />
                  )}

                  <div className="court-reservation-modal__buttons">
                    {tickets.length > 0 && isSaleAvailable() && (
                      <EAButton
                        title={getBuyButtonTitle()}
                        onClick={handleBuyTicket}
                        startIcon={<MdShoppingCart />}
                        variant="filled"
                        color="primary"
                        fullWidth
                      />
                    )}

                    {!isSaleAvailable() && (
                      <div className="court-reservation-modal__sale-unavailable">
                        <p>{getSaleUnavailableMessage()}</p>
                      </div>
                    )}

                    {allowToSignByPass &&
                      (isLoggedIn ? (
                        <EAButton
                          title="Zapisz się karnetem"
                          onClick={handleSignWithPass}
                          startIcon={<AiOutlineUserAdd />}
                          variant="outlined"
                          fullWidth
                          disabled={passLoading}
                        />
                      ) : (
                        <span className="caption text-centered">
                          <EALink to={LOGIN_PAGE} title="Zaloguj się," color="primary" />
                          aby móc zapisać się karnetem.
                        </span>
                      ))}
                  </div>
                </>
              )}

              {currentStep === "tickets" && (
                <CourtTicketSelection
                  exerciseId={exerciseId}
                  exerciseDetails={exerciseDetails}
                  tickets={tickets}
                  ticketAccess={ticketAccess}
                  ticketLimit={ticketLimit}
                  onTicketsAdded={handleTicketsAdded}
                  onBack={handleBackToInfo}
                  saleStep={saleStep}
                />
              )}

              {currentStep === "pass-selection" && (
                <PassSelection
                  exerciseDetails={exerciseDetails}
                  onPassSelected={handlePassSelected}
                  onBack={handleBackToInfo}
                  alert={passAlert}
                  loading={passLoading}
                />
              )}

              {currentStep === "pass-confirmation" && renderPassConfirmationStep()}
            </div>
          ) : (
            <p>Nie udało się załadować danych.</p>
          )}
        </div>
      </EAModal>

      <EAModal
        isOpen={loginRequiredModalOpen}
        title={t("ta_opcja_dostepna_tylko_po_zalogowaniu")}
        onClose={() => setLoginRequiredModalOpen(false)}
        acceptButtonLabel={t("przejdz_do_logowania_rejestracji")}
        cancelButtonLabel={t("zamknij")}
        onAccept={() => navigate(LOGIN_PAGE_WITH_RETURN)}
      >
        <p>{t("dedykowane_dla_stalych_klientow_info")}</p>
      </EAModal>
    </>
  );
};

export default CourtReservationModal;
